import { useState, useMemo } from 'react';
import { ChevronsUpDown, Search, Loader2, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TokenLogo } from '@/components/shared/token-logo';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  getNetworkDisplayName,
  getTokenBlockExplorerUrl,
  nativeTokenPseudoAddress,
  type OrganizationToken,
} from '@mls-wallet/shared-types';

export interface TokenSelectorValue {
  type: 'any' | 'specific';
  token?: OrganizationToken;
}

interface TokenSelectorProps {
  tokens: OrganizationToken[];
  isLoading?: boolean;
  value?: TokenSelectorValue;
  onSelect: (token: OrganizationToken | null) => void;
  placeholder?: string;
  className?: string;
  size?: 'default' | 'sm' | 'lg';
  disabled?: boolean;
  noBalanceTokenIds?: string[];
}

const isNativeToken = (token: OrganizationToken) =>
  token.address.toLowerCase() === nativeTokenPseudoAddress.toLowerCase();

const getTokenCaption = (token: OrganizationToken) =>
  isNativeToken(token) ? `Native ${token.symbol}` : token.name;

/**
 * Selects a token and marks selected token IDs omitted by account-scoped
 * balance results through `noBalanceTokenIds`.
 */
export function TokenSelector({
  tokens,
  isLoading = false,
  value,
  onSelect,
  placeholder,
  className,
  size = 'default',
  disabled = false,
  noBalanceTokenIds = [],
}: TokenSelectorProps) {
  const [open, setOpen] = useState(false);
  const [tempValue, setTempValue] = useState<TokenSelectorValue>(
    value || { type: 'any' }
  );
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTokens = useMemo(() => {
    if (!searchQuery) return tokens;

    const query = searchQuery.toLowerCase();
    return tokens.filter(
      (token) =>
        token.symbol.toLowerCase().includes(query) ||
        (getTokenCaption(token)?.toLowerCase().includes(query) ?? false)
    );
  }, [searchQuery, tokens]);

  /** Converts the omitted-token policy state into the token's balance status. */
  const hasNoBalance = (token: OrganizationToken) =>
    noBalanceTokenIds.includes(token.id);

  const getDisplayValue = () => {
    if (!value || value.type === 'any') {
      return placeholder || 'Any token';
    }

    if (value.type === 'specific' && value.token) {
      return (
        <div className="flex items-center gap-2">
          <TokenLogo
            symbol={value.token.symbol}
            logoUrl={value.token.logoUrl ?? undefined}
            size="sm"
          />
          <span>{value.token.symbol}</span>
          {hasNoBalance(value.token) && (
            <Badge
              variant="outline"
              className="font-normal text-muted-foreground"
            >
              No balance
            </Badge>
          )}
        </div>
      );
    }

    return placeholder || 'Any token';
  };

  const handleTokenSelect = (token: OrganizationToken) => {
    setTempValue({
      type: 'specific',
      token,
    });
  };

  const handleSave = () => {
    if (tempValue.type === 'any') {
      onSelect(null);
    } else {
      onSelect(tempValue.token ?? null);
    }
    setOpen(false);
  };

  const handleCancel = () => {
    setTempValue(value || { type: 'any' });
    setSearchQuery('');
    setOpen(false);
  };

  const handleOpen = () => {
    if (disabled) return;
    setTempValue(value || { type: 'any' });
    setSearchQuery('');
    setOpen(true);
  };

  return (
    <>
      <Button
        variant="outline"
        role="combobox"
        aria-expanded={open}
        size={size}
        disabled={disabled}
        className={cn(
          'w-full justify-between',
          'border-input',
          !value && 'text-muted-foreground',
          size === 'lg' && 'text-base',
          className
        )}
        onClick={handleOpen}
      >
        {getDisplayValue()}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Select token</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <RadioGroup
              value={tempValue.type}
              onValueChange={(type) => {
                if (type === 'any') {
                  setTempValue({ type: 'any' });
                } else if (tempValue.token) {
                  setTempValue({ type: 'specific', token: tempValue.token });
                } else {
                  setTempValue({ type: 'specific' });
                }
              }}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="any" id="any-token" />
                <Label htmlFor="any-token">Any token</Label>
              </div>
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="specific"
                    id="specific-token"
                    disabled={isLoading || tokens.length === 0}
                  />
                  <Label
                    htmlFor="specific-token"
                    className={
                      isLoading || tokens.length === 0
                        ? 'text-muted-foreground'
                        : ''
                    }
                  >
                    Specific token
                  </Label>
                </div>
                {!isLoading && tokens.length === 0 && (
                  <p className="text-xs text-muted-foreground ml-6">
                    No tokens found for selected blockchain network and accounts
                  </p>
                )}
              </div>
            </RadioGroup>

            {tempValue.type === 'specific' && (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search tokens..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <ScrollArea className="h-[300px] w-full rounded-md border p-2">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {filteredTokens.map((token) => {
                        const isNative = isNativeToken(token);
                        const tokenCaption = getTokenCaption(token);
                        const explorerUrl = isNative
                          ? null
                          : getTokenBlockExplorerUrl(
                              token.address,
                              token.networkId
                            );

                        return (
                          <div
                            key={token.id}
                            className={cn(
                              'flex items-center w-full rounded-md transition-colors',
                              'hover:bg-accent',
                              tempValue.token?.id === token.id && 'bg-accent'
                            )}
                          >
                            {/* Clickable area for token selection */}
                            <button
                              onClick={() => handleTokenSelect(token)}
                              className="flex items-center gap-3 flex-1 min-w-0 text-left p-3 bg-transparent"
                            >
                              <TokenLogo
                                symbol={token.symbol}
                                logoUrl={token.logoUrl ?? undefined}
                                size="md"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 font-medium">
                                  <span>{token.symbol}</span>
                                  {hasNoBalance(token) && (
                                    <Badge
                                      variant="outline"
                                      className="font-normal text-muted-foreground"
                                    >
                                      No balance
                                    </Badge>
                                  )}
                                </div>
                                {tokenCaption && (
                                  <div className="text-sm text-muted-foreground truncate">
                                    {tokenCaption}
                                  </div>
                                )}
                              </div>
                              {tempValue.token?.id === token.id && (
                                <div className="w-2 h-2 bg-primary rounded-full shrink-0" />
                              )}
                            </button>

                            {explorerUrl && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(
                                        explorerUrl,
                                        '_blank',
                                        'noopener,noreferrer'
                                      );
                                    }}
                                    className="p-2 mr-1 rounded bg-transparent hover:bg-muted transition-colors shrink-0"
                                    aria-label="View on block explorer"
                                  >
                                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                  <p className="font-mono text-xs">
                                    {token.address}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        );
                      })}
                      {filteredTokens.length === 0 && !isLoading && (
                        <div className="text-center py-6 text-muted-foreground">
                          {tokens.length === 0
                            ? 'No tokens available'
                            : `No tokens found matching "${searchQuery}"`}
                        </div>
                      )}
                    </div>
                  )}
                </ScrollArea>

                {tempValue.token && (
                  <div className="p-3 bg-accent rounded-md">
                    <div className="text-sm font-medium mb-2">Selected:</div>
                    <div className="flex items-center gap-2">
                      <TokenLogo
                        symbol={tempValue.token.symbol}
                        logoUrl={tempValue.token.logoUrl ?? undefined}
                        size="sm"
                      />
                      <div className="text-sm text-muted-foreground">
                        {tempValue.token.symbol}
                        {getTokenCaption(tempValue.token) &&
                          ` - ${getTokenCaption(tempValue.token)}`}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      <span className="capitalize">
                        {getNetworkDisplayName(tempValue.token.networkId)}
                      </span>
                      {!isNativeToken(tempValue.token) && (
                        <>
                          {' · '}
                          <span className="font-mono">
                            {tempValue.token.address}
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
